import { useLazyChangeTransactionStateQuery } from "@/modules/transaction/services";
import { setTransaction } from "@/modules/transaction/state";
import { OperationNextState } from "@/shared/common/enums";
import { useAppDispatch, useAppSelector, useNotify } from "@/shared/hooks";
import { setActiveTransactionId } from "@/shared/state/common";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  refetch: () => void;
  isDeleteModalOpen: boolean;
  setIsDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
}

export const DeleteTransactionModal = ({
  refetch,
  isDeleteModalOpen,
  setIsDeleteModalOpen,
}: Props) => {
  const { t } = useTranslation();
  const { showErrorSnackbar, showSuccessSnackbar } = useNotify();
  const dispatch = useAppDispatch();
  const transaction = useAppSelector((state) => state.transactions.transaction);

  const [changeState, { isLoading: isDeleteTransactionLoading }] = useLazyChangeTransactionStateQuery();

  const handleDeleteTransaction = () => {
    if (transaction?.id) {
      changeState({
        id: transaction.id,
        data: {
          transactionType: transaction.transactionType,
          nextState: OperationNextState.Delete,
        },
      })
        .unwrap()
        .then(() => {
          setIsDeleteModalOpen(false);
          dispatch(setTransaction({}));
          refetch();
          showSuccessSnackbar(t("alerts.transferHasBeenSuccessfullyDeleted"));
        })
        .catch((err) => {
          showErrorSnackbar(err);
          setIsDeleteModalOpen(false);
          dispatch(setTransaction({}));
        }).finally(() => {
        dispatch(setActiveTransactionId(null));
      });
    }
  };

  return (
    <Modal
      isOpen={isDeleteModalOpen}
      onClose={() => setIsDeleteModalOpen(false)}
      onSubmit={handleDeleteTransaction}
      buttonProps={{
        confirm: {
          buttonText: t("general.delete"),
          type: "danger",
          isLoading: isDeleteTransactionLoading,
          disabled: isDeleteTransactionLoading,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
      titleProps={{
        title: t("transactionHistoryTable.deleteModal.title"),
        size: "large"
      }}
    >
    </Modal>
  );
};
